import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ModuleAlertsPage from '../../shared/components/ModuleAlertsPage';
import localApiAdapter from '../../core/services/localApiAdapter';
import { formatHrDate, SAUDI_HR_CONFIG } from '../../shared/utils/hrSaudiDefaults';
import { normalizeHrEmployeesResponse } from './hrListPageUtils';
import { ALL_HR_DATA_REFRESH_EVENTS, useHrApiRefreshListener } from './hrRefreshEvents';
import { ACCIDENT_STATUS_VALUES } from '../../shared/utils/hrApiRecordNormalizers';

const daysUntil = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return null;
  return Math.ceil((d - Date.now()) / (1000 * 60 * 60 * 24));
};

const HrAlertsPage = () => {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.ressources_humaines.pages.hrAlertsPage.${key}`, opts);
  const today = formatHrDate(new Date());
  const threshold = SAUDI_HR_CONFIG.salaryAlertThresholdSAR;
  const wpsDay = SAUDI_HR_CONFIG.wpsDeadlineDay;
  const nitaqatTarget = SAUDI_HR_CONFIG.nitaqatTargetPct;

  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadAlerts = useCallback(async () => {
    try {
      setLoading(true);
      const [employeesRes, leavesRes, accidentsRes] = await Promise.all([
        localApiAdapter.get('/hr/employees'),
        localApiAdapter.get('/hr/leaves'),
        localApiAdapter.get('/hr/accidents'),
      ]);

      const employees = normalizeHrEmployeesResponse(employeesRes);
      const leaves = Array.isArray(leavesRes?.data ?? leavesRes) ? (leavesRes?.data ?? leavesRes) : [];
      const accidents = Array.isArray(accidentsRes?.data ?? accidentsRes) ? (accidentsRes?.data ?? accidentsRes) : [];

      const mapped = [];

      const pendingLeaves = leaves.filter((l) => ['pending_manager', 'pending_hr', 'en_attente'].includes(l.status));
      if (pendingLeaves.length) {
        mapped.push({
          id: 'leaves-pending',
          type: 'warning',
          priority: 'high',
          title: p('alert_leaves_title'),
          message: p('alert_leaves_message', { count: pendingLeaves.length, defaultValue: `${pendingLeaves.length} طلب إجازة بانتظار الموافقة` }),
          date: today,
          read: false,
        });
      }

      const expiringContracts = employees.filter((e) => {
        const days = daysUntil(e.finEssai || e.dateSortie);
        return days != null && days >= 0 && days <= 30;
      });
      if (expiringContracts.length) {
        mapped.push({
          id: 'contracts-expiring',
          type: 'error',
          priority: 'critical',
          title: p('alert_contract_expiry_title'),
          message: p('alert_contract_expiry_message', { count: expiringContracts.length, defaultValue: `${expiringContracts.length} عقد ينتهي خلال 30 يوماً` }),
          date: today,
          read: false,
        });
      }

      const highSalary = employees.filter((e) => (parseFloat(e.salaireBase) || 0) >= threshold);
      if (highSalary.length) {
        mapped.push({
          id: 'salary-threshold',
          type: 'info',
          priority: 'medium',
          title: p('alert_salary_title'),
          message: p('alert_salary_message', { threshold: threshold.toLocaleString('ar-SA'), count: highSalary.length }),
          date: today,
          read: true,
        });
      }

      const openAccidents = accidents.filter((a) =>
        [ACCIDENT_STATUS_VALUES.declared, ACCIDENT_STATUS_VALUES.investigation, 'déclaré', 'enquête'].includes(a.status),
      );
      if (openAccidents.length) {
        mapped.push({
          id: 'accidents-open',
          type: 'warning',
          priority: 'high',
          title: p('alert_documents_title'),
          message: p('alert_documents_message', { count: openAccidents.length, defaultValue: `${openAccidents.length} حادث قيد المتابعة` }),
          date: today,
          read: false,
        });
      }

      mapped.push({
        id: 'nitaqat',
        type: 'warning',
        priority: 'high',
        title: p('alert_nitaqat_title'),
        message: p('alert_nitaqat_message', { pct: 22, target: nitaqatTarget }),
        date: today,
        read: false,
      });

      mapped.push({
        id: 'wps',
        type: 'warning',
        priority: 'high',
        title: p('alert_wps_title'),
        message: p('alert_wps_message', { day: wpsDay }),
        date: today,
        read: false,
      });

      mapped.push({
        id: 'gosi',
        type: 'info',
        priority: 'medium',
        title: p('alert_gosi_title'),
        message: p('alert_gosi_message'),
        date: today,
        read: true,
      });

      setAlerts(mapped);
    } catch (error) {
      console.error('Error loading HR alerts:', error);
      setAlerts([]);
    } finally {
      setLoading(false);
    }
  }, [p, today, threshold, wpsDay, nitaqatTarget]);

  useEffect(() => {
    void loadAlerts();
  }, [loadAlerts]);

  useHrApiRefreshListener(ALL_HR_DATA_REFRESH_EVENTS, loadAlerts);

  const fallbackAlerts = useMemo(() => ([
    {
      id: 1,
      type: 'warning',
      priority: 'high',
      title: p('alert_leaves_title'),
      message: p('alert_leaves_message'),
      date: today,
      read: false,
    },
  ]), [p, today]);

  return (
    <ModuleAlertsPage
      moduleName={p('module_name')}
      moduleIcon={Users}
      alerts={loading ? [] : (alerts.length ? alerts : fallbackAlerts)}
      loading={loading}
    />
  );
};

export default HrAlertsPage;
